// Nudge matrix config — NEW console page (18H §4.2 / 17_ §6, D-22/D-33).
// Operator-editable tier × nudge_type matrix: enable/disable, copy_ref
// (the body lives in TPRM7 seed rows — this is a reference only, this server
// never sends), and per-org rate caps. One row per (tier, nudge_type).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Save, Plus, PlayCircle } from 'lucide-react';
import { Kpi } from '@/components/shared/Kpi';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { useToast } from '@/components/shared/ToastProvider';
import {
  getNudgeMatrix, upsertNudgeMatrixRow, runLifecycle, nudgeTypeLabel, tierLabel,
  SAAS_LICENSE_TIERS, type NudgeMatrixConfigRow,
} from '@/lib/api/trust';

// Must stay the engine's evaluable vocabulary (trust-lifecycle.service.ts
// evaluateTrigger): a type it does not know is inert. 'trial_expiry' is N11
// (D-53) — its DECISION runs, its DELIVERY is held until OF-066.
const KNOWN_NUDGE_TYPES = [
  'single_admin', 'staleness_digest', 'anniversary', 'access_review',
  'trust_pro_upsell', 'domain_grace', 'subprocessor_breach', 'trial_expiry',
];

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const inputSm: React.CSSProperties = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12.5, outline: 'none' };

interface Draft {
  enabled: boolean;
  copy_ref: string;
  rate_cap_per_period: string;
  rate_cap_period: 'day' | 'week' | 'month';
  dirty: boolean;
}

function rowKey(tier: string, nudgeType: string) {
  return `${tier}::${nudgeType}`;
}

function toDraft(r: NudgeMatrixConfigRow): Draft {
  return {
    enabled: r.enabled,
    copy_ref: r.copy_ref || '',
    rate_cap_per_period: r.rate_cap_per_period != null ? String(r.rate_cap_per_period) : '',
    rate_cap_period: (r.rate_cap_period as Draft['rate_cap_period']) || 'month',
    dirty: false,
  };
}

export function NudgeMatrixPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<NudgeMatrixConfigRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [runningEngine, setRunningEngine] = useState(false);
  const [addForm, setAddForm] = useState({ tier: 'trust_base', nudge_type: 'staleness_digest' });

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const data = await getNudgeMatrix();
      setRows(data || []);
      const d: Record<string, Draft> = {};
      for (const r of data || []) d[rowKey(r.tier, r.nudge_type)] = toDraft(r);
      setDrafts(d);
      setState('ready');
    } catch (err) {
      setRows([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byTier = useMemo(() => {
    const map = new Map<string, NudgeMatrixConfigRow[]>();
    for (const r of rows) {
      const list = map.get(r.tier) || [];
      list.push(r);
      map.set(r.tier, list);
    }
    return map;
  }, [rows]);

  const kpis = useMemo(() => ({
    total: rows.length,
    enabled: rows.filter((r) => r.enabled).length,
    tiersConfigured: byTier.size,
  }), [rows, byTier]);

  const setDraft = (key: string, patch: Partial<Draft>) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || toDraft({ tier: '', nudge_type: '', enabled: true, copy_ref: null, rate_cap_per_period: null, rate_cap_period: null } as any)), ...patch, dirty: true } }));
  };

  const saveRow = async (tier: string, nudgeType: string) => {
    const key = rowKey(tier, nudgeType);
    const d = drafts[key];
    if (!d) return;
    setSavingKey(key);
    try {
      const saved = await upsertNudgeMatrixRow({
        tier, nudge_type: nudgeType, enabled: d.enabled,
        copy_ref: d.copy_ref || null,
        rate_cap_per_period: d.rate_cap_per_period ? parseInt(d.rate_cap_per_period, 10) : null,
        rate_cap_period: d.rate_cap_per_period ? d.rate_cap_period : null,
      });
      setRows((prev) => {
        const exists = prev.some((r) => r.tier === tier && r.nudge_type === nudgeType);
        return exists ? prev.map((r) => (r.tier === tier && r.nudge_type === nudgeType ? saved : r)) : [...prev, saved];
      });
      setDrafts((prev) => ({ ...prev, [key]: toDraft(saved) }));
      toast({ kind: 'success', title: `Saved ${nudgeTypeLabel(nudgeType)} for ${tierLabel(tier)}.` });
    } catch (err) {
      toast({ kind: 'error', title: 'Save failed', body: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setSavingKey(null);
    }
  };

  const addRule = async () => {
    const key = rowKey(addForm.tier, addForm.nudge_type);
    if (rows.some((r) => r.tier === addForm.tier && r.nudge_type === addForm.nudge_type)) {
      toast({ kind: 'warning', title: 'That rule already exists — edit it below instead.' });
      return;
    }
    setSavingKey(key);
    try {
      const saved = await upsertNudgeMatrixRow({ tier: addForm.tier, nudge_type: addForm.nudge_type, enabled: true });
      setRows((prev) => [...prev, saved]);
      setDrafts((prev) => ({ ...prev, [key]: toDraft(saved) }));
      toast({ kind: 'success', title: 'Rule added.' });
    } catch (err) {
      toast({ kind: 'error', title: 'Failed to add rule', body: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setSavingKey(null);
    }
  };

  const runEngine = async () => {
    setRunningEngine(true);
    try {
      const result = await runLifecycle();
      // heldForBridge: due N11 trial-expiry nudges that cannot be delivered yet (OF-066).
      const held = result.heldForBridge ? `, ${result.heldForBridge} held (delivery not built)` : '';
      toast({ kind: 'success', title: `Nudge engine ran: ${result.opportunitiesOpened} opened, ${result.opportunitiesNudged} nudged, ${result.rateCapped} rate-capped${held}.` });
    } catch (err) {
      toast({ kind: 'error', title: 'Lifecycle run failed', body: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setRunningEngine(false);
    }
  };

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      {[0, 1].map((i) => <div key={i} style={{ height: 160, borderRadius: 12, background: 'var(--bg-muted)', marginBottom: 16 }} />)}
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Nudge matrix</h1>
          <p className="page-subtitle">Operator-editable tier → nudge enablement, copy reference, and per-org rate caps (D-22/D-33). Delivery is TPRM7-side — this server never sends.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
          <button onClick={runEngine} disabled={runningEngine} style={{ ...btnSec, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' }}>
            <PlayCircle size={13} /> {runningEngine ? 'Running…' : 'Run engine now'}
          </button>
        </div>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={false}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 22 }}>
          <Kpi label="Configured rules" value={kpis.total} icon="sliders-horizontal" />
          <Kpi label="Enabled" value={kpis.enabled} icon="bell" accent="#16A34A" />
          <Kpi label="Tiers configured" value={`${kpis.tiersConfigured}/${SAAS_LICENSE_TIERS.length}`} icon="layers" />
        </div>

        {/* Add-rule form */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Add a rule</span></div>
          <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 4 }}>Tier</label>
              <select value={addForm.tier} onChange={(e) => setAddForm((p) => ({ ...p, tier: e.target.value }))} style={{ ...inputSm, minWidth: 130 }}>
                {SAAS_LICENSE_TIERS.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 4 }}>Nudge type</label>
              <select value={addForm.nudge_type} onChange={(e) => setAddForm((p) => ({ ...p, nudge_type: e.target.value }))} style={{ ...inputSm, minWidth: 180 }}>
                {KNOWN_NUDGE_TYPES.map((t) => <option key={t} value={t}>{nudgeTypeLabel(t)}</option>)}
              </select>
            </div>
            <button onClick={addRule} disabled={savingKey === rowKey(addForm.tier, addForm.nudge_type)} style={{ ...btnSec, background: 'var(--accent-light)', color: 'var(--accent)', border: '1px solid var(--accent-muted)' }}>
              <Plus size={13} /> Add rule
            </button>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Unrecognised nudge types are safely inert until the engine gains a case for them.</span>
          </div>
        </div>

        {SAAS_LICENSE_TIERS.map((tier) => {
          const tierRows = byTier.get(tier) || [];
          return (
            <div className="card" key={tier} style={{ marginBottom: 16 }}>
              <div className="card-header"><span className="card-title">{tierLabel(tier)}</span></div>
              {tierRows.length === 0 ? (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No rules configured for this tier yet — add one above.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Nudge', 'Enabled', 'Copy ref', 'Rate cap', 'Period', ''].map((h) => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tierRows.map((r) => {
                        const key = rowKey(r.tier, r.nudge_type);
                        const d = drafts[key] || toDraft(r);
                        return (
                          <tr key={key} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 500 }}>{nudgeTypeLabel(r.nudge_type)}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <input type="checkbox" checked={d.enabled} onChange={(e) => setDraft(key, { enabled: e.target.checked })} style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <input type="text" value={d.copy_ref} onChange={(e) => setDraft(key, { copy_ref: e.target.value })} placeholder="template ref" style={{ ...inputSm, width: 160 }} />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <input type="number" min={0} value={d.rate_cap_per_period} onChange={(e) => setDraft(key, { rate_cap_per_period: e.target.value })} placeholder="none" style={{ ...inputSm, width: 70 }} />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <select value={d.rate_cap_period} onChange={(e) => setDraft(key, { rate_cap_period: e.target.value as Draft['rate_cap_period'] })} style={inputSm}>
                                <option value="day">day</option>
                                <option value="week">week</option>
                                <option value="month">month</option>
                              </select>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <button
                                onClick={() => saveRow(r.tier, r.nudge_type)}
                                disabled={savingKey === key || !d.dirty}
                                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: d.dirty ? 'var(--accent)' : 'var(--surface)', color: d.dirty ? '#fff' : 'var(--text-muted)', cursor: d.dirty ? 'pointer' : 'default', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              >
                                <Save size={12} /> {savingKey === key ? 'Saving…' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </StateFrame>
    </div>
  );
}

export default NudgeMatrixPage;
